import React, { useState, useEffect } from "react";
import { FiEdit2, FiTrash2, FiPlus } from "react-icons/fi";
import client from "../../api/client";
import Modal from "../../components/Modal";

function fileToDataUrl(file) {
  return new Promise((resolve, reject) => {
    const fr = new FileReader();
    fr.onload = () => resolve(fr.result);
    fr.onerror = reject;
    fr.readAsDataURL(file);
  });
}

function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showModal, setShowModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [formData, setFormData] = useState({
    id: null,
    name: "",
    images: [], // items are either string (existing URL/base64) or { file, preview }
    colors: [],
    sizes: [],
    category: "",
    price: "",
    discountPercent: 0,
    salePrice: "",
    rating: "",
    description: "",
    fabric: "",
    manufacturer: "",
    occasion: "",
    washCare: "",
    productType: "",
    work: "",
    stock: "",
    status: "Active",
  });
  const [editingId, setEditingId] = useState(null);
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [selectedColorHex, setSelectedColorHex] = useState("#0a2845000");
  const [currentPage, setCurrentPage] = useState(1);
  const ITEMS_PER_PAGE = 10;

  const totalPages = Math.ceil(products.length / ITEMS_PER_PAGE);
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = startIndex + ITEMS_PER_PAGE;
  const currentProducts = products.slice(startIndex, endIndex);

  console.log(products, 'rrsxd');

  const predefinedColors = [
    { name: "Red", hex: "#FF0000" },
    { name: "Blue", hex: "#0a28450FF" },
    { name: "Green", hex: "#00AA00" },
    { name: "Yellow", hex: "#FFFF00" },
    { name: "Black", hex: "#0a2845000" },
    { name: "White", hex: "#FFFFFF" },
    { name: "Pink", hex: "#FFC0CB" },
    { name: "Orange", hex: "#FFA500" },
    { name: "Purple", hex: "#800080" },
    { name: "Gray", hex: "#808080" },
  ];

  useEffect(() => {
    let mounted = true;
    async function fetchAll() {
      try {
        setLoading(true);
        setError("");
        const [prodRes, catRes] = await Promise.all([
          client.get("/catalog/products"),
          client.get("/catalog/categories"),
        ]);
        if (!mounted) return;
        // client returns { data } shape
        const prodData = prodRes.data?.items ?? prodRes.data ?? [];
        const catData = catRes.data?.items ?? catRes.data ?? [];

        // ensure arrays and normalize images/colors
        const normalizedProducts = (Array.isArray(prodData) ? prodData : []).map((p) => ({
          ...p,
          images: Array.isArray(p.images) ? p.images.filter(Boolean) : [],
          colors: Array.isArray(p.colors) ? p.colors : [],
        }));

        setProducts(normalizedProducts);
        setCategories(Array.isArray(catData) ? catData : []);
      } catch (err) {
        setError(err.message || "Failed to load products");
      } finally {
        setLoading(false);
      }
    }
    fetchAll();
    return () => {
      mounted = false;
    };
  }, []);

  const handleInputChange = (e) => {
    const { name, value, type, files } = e.target;

    if (type === "file") {
      // convert to objects with preview for client-side preview
      const newImages = Array.from(files).map((f) => ({ file: f, preview: URL.createObjectURL(f) }));
      setFormData((prev) => ({
        ...prev,
        images: [...(prev.images || []), ...newImages],
      }));
      return;
    }

    if (name === "sizes") {
      // accept comma-separated sizes and normalize to array of trimmed strings
      const parts = value
        .split(",")
        .map((s) => s.trim())
        .filter(Boolean);
      setFormData((prev) => ({ ...prev, sizes: parts }));
      return;
    }

    if (name === "price" || name === "discountPercent") {
      const rawPrice = name === "price" ? parseFloat(value || 0) : parseFloat(formData.price || 0);
      const rawDiscount = name === "discountPercent" ? parseFloat(value || 0) : parseFloat(formData.discountPercent || 0);
      const priceVal = name === "price" ? rawPrice : parseFloat(formData.price || 0);
      const discountVal = name === "discountPercent" ? rawDiscount : parseFloat(formData.discountPercent || 0);
      const salePrice = priceVal - (priceVal * discountVal) / 100;
      setFormData((prev) => ({
        ...prev,
        [name]: name === "price" ? rawPrice : rawDiscount,
        salePrice: Number.isFinite(salePrice) ? salePrice.toFixed(2) : "",
      }));
      return;
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const addColor = (color) => {
    if (!formData.colors.some((c) => c.hex === color.hex)) {
      setFormData((prev) => ({ ...prev, colors: [...prev.colors, color] }));
    }
    setShowColorPicker(false);
  };

  const removeColor = (colorHex) => {
    setFormData((prev) => ({ ...prev, colors: prev.colors.filter((c) => c.hex !== colorHex) }));
  };

  const removeImage = (index) => {
    const item = formData.images[index];
    if (item && item.preview && item.file) {
      URL.revokeObjectURL(item.preview);
    }
    setFormData((prev) => ({ ...prev, images: prev.images.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setLoading(true);
      setError("");
      const payload = { ...formData };

      // prepare images: existing strings preserved, file objects converted to base64
      const existingUrls = (payload.images || []).filter((i) => typeof i === "string");
      const fileObjs = (payload.images || []).filter((i) => i && typeof i === "object" && i.file);
      const fileBase64 = await Promise.all(fileObjs.map((f) => fileToDataUrl(f.file)));
      payload.images = [...existingUrls, ...fileBase64];

      // send category as name (backend resolveCategory will convert)
      if (!payload.name && payload.title) payload.name = payload.title;
      const path = editingId ? `/catalog/products/${editingId}` : `/catalog/products`;
      const res = editingId ? await client.put(path, payload) : await client.post(path, payload);
      const item = res.data?.item ?? res.data;
      if (item) {
        if (editingId) setProducts((prev) => prev.map((p) => (String(p._id) === String(editingId) ? item : p)));
        else setProducts((prev) => [item, ...prev]);
      }
      resetForm();
    } catch (err) {
      setError(err.message || "Save failed");
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (product) => {
    const editing = {
      id: product._id || null,
      name: product.title || product.name || "",
      images: Array.isArray(product.images) ? product.images.slice() : [],
      colors: Array.isArray(product.colors) ? product.colors.slice() : [],
      sizes: Array.isArray(product.sizes) ? product.sizes.slice() : [],
      category: "",
      price: product.price ?? "",
      discountPercent: product.discountPercent ?? 0,
      salePrice: product.salePrice ?? "",
      rating: product.rating ?? "",
      description: product.description ?? "",
      fabric: product.fabric ?? "",
      manufacturer: product.manufacturer ?? "",
      occasion: product.occasion ?? "",
      washCare: product.washCare ?? "",
      productType: product.productType ?? "",
      work: product.work ?? "",
      stock: product.stock ?? "",
      status: product.active === false ? "Inactive" : "Active",
    };

    // determine readable category name for the select (handles populated objects or ids)
    if (Array.isArray(product.categories) && product.categories.length) {
      const first = product.categories[0];
      // if populated object with name
      if (first && typeof first === "object") {
        editing.category = first.name || first.title || "";
      } else {
        // primitive id -> lookup in fetched categories
        const found = categories.find((c) => String(c._id) === String(first));
        if (found) editing.category = found.name;
      }
    } else if (product.category) {
      // fallback when backend returns `category` field
      editing.category = typeof product.category === "object" ? (product.category.name || product.category.title || "") : product.category;
    }

    setFormData(editing);
    setEditingId(product._id || product.id);
    setShowModal(true);
  };

  const handleDeleteClick = (id) => {
    setDeletingId(id);
    setShowDeleteModal(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deletingId) return;
    try {
      setLoading(true);
      setError("");
      await client.delete(`/catalog/products/${deletingId}`);
      setProducts((prev) => prev.filter((prod) => String(prod._id) !== String(deletingId)));
    } catch (err) {
      setError(err.message || "Delete failed");
    } finally {
      setLoading(false);
    }
    setShowDeleteModal(false);
    setDeletingId(null);
  };

  const resetForm = () => {
    (formData.images || []).forEach((i) => { if (i && i.preview && i.file) URL.revokeObjectURL(i.preview); });
    setFormData({
      id: null,
      name: "",
      images: [],
      colors: [],
      sizes: [],
      category: "",
      price: "",
      discountPercent: 0,
      salePrice: "",
      rating: "",
      description: "",
      fabric: "",
      manufacturer: "",
      occasion: "",
      washCare: "",
      productType: "",
      work: "",
      stock: "",
      status: "Active",
    });
    setEditingId(null);
    setShowModal(false);
    setShowColorPicker(false);
  };

  function getCategoryName(product) {
    // if product.categories is populated with objects
    if (Array.isArray(product.categories) && product.categories.length) {
      const first = product.categories[0];
      if (first && typeof first === "object") {
        return first.name || first.title || first.slug || String(first._id) || "";
      }
      // primitive id value
      const id = String(first);
      const found = categories.find((c) => String(c._id) === id);
      return found ? found.name : id;
    }

    // product.category might be an object or a string
    if (product.category && typeof product.category === "object") {
      return product.category.name || product.category.title || "";
    }

    return product.category || "";
  }

  return (
    <div>
      {/* Loading / Error state */}
      {loading && (
        <div style={{ marginBottom: "10px", color: "#555" }}>Loading…</div>
      )}
      {error && (
        <div style={{ marginBottom: "10px", color: "#d93025" }}>{error}</div>
      )}
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          marginBottom: "20px",
        }}
      >
        <h1 style={{ fontSize: "24px", fontWeight: 700, margin: 0, color: '#2b4d6e'  }}>
          Products
        </h1>
        <button
          className="x_btn x_btn-primary"
          onClick={() => {
            resetForm();
            setShowModal(true);
          }}
        >
          <FiPlus size={16} /> Add Product
        </button>
      </div>

      {/* Modal */}
      <div className={`x_modal-overlay ${showModal ? "x_active" : ""}`} style={{ overflowY: "auto" }}>
        <div className="x_modal-content" style={{ maxWidth: "700px" }}>
          <div className="x_modal-header">
            <h2>{editingId ? "Edit Product" : "Add New Product"}</h2>
            <button className="x_modal-close" onClick={resetForm}>
              ×
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="x_modal-body" style={{ maxHeight: "70vh", overflowY: "auto" }}>
              {/* Images Section */}
              <div className="x_form-group">
                <label className="x_form-label">
                  Product Images (Multi-Select)
                </label>
                <input
                  type="file"
                  name="images"
                  className="x_form-control"
                  onChange={handleInputChange}
                  accept="image/*"
                  multiple
                />
                {formData.images && formData.images.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginTop: "10px",
                    }}
                  >
                    {formData.images.map((img, idx) => (
                      <div
                        key={idx}
                        style={{
                          position: "relative",
                          width: "60px",
                          height: "60px",
                        }}
                      >
                        {typeof img === "object" && img.preview ? (
                          <>
                            <img
                              src={img.preview}
                              alt={img.file?.name || "preview"}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              style={{
                                position: "absolute",
                                top: "-8px",
                                right: "-8px",
                                backgroundColor: "#ff4444",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            >
                              ×
                            </button>
                          </>
                        ) : (
                          <>
                            <img
                              src={img}
                              alt={`img-${idx}`}
                              style={{
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                borderRadius: "4px",
                              }}
                            />
                            <button
                              type="button"
                              onClick={() => removeImage(idx)}
                              style={{
                                position: "absolute",
                                top: "-8px",
                                right: "-8px",
                                backgroundColor: "#ff4444",
                                color: "white",
                                border: "none",
                                borderRadius: "50%",
                                width: "20px",
                                height: "20px",
                                cursor: "pointer",
                              }}
                            >
                              ×
                            </button>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Colors Section */}
              <div className="x_form-group">
                <label className="x_form-label">
                  Colors (Multi-Select with Color Picker)
                </label>
                <div
                  style={{ display: "flex", gap: "10px", alignItems: "center" }}
                >
                  <button
                    type="button"
                    className="x_btn x_btn-primary"
                    onClick={() => setShowColorPicker(!showColorPicker)}
                    style={{ padding: "8px 12px", fontSize: "14px" }}
                  >
                    Add Color
                  </button>
                </div>

                {showColorPicker && (
                  <div
                    style={{
                      marginTop: "10px",
                      padding: "10px",
                      backgroundColor: "#f9f9f9",
                      borderRadius: "4px",
                      border: "1px solid #ddd",
                    }}
                  >
                    <div style={{ marginBottom: "10px" }}>
                      <label
                        style={{
                          fontSize: "12px",
                          display: "block",
                          marginBottom: "5px",
                        }}
                      >
                        Select Color:
                      </label>
                      <input
                        type="color"
                        value={selectedColorHex}
                        onChange={(e) => setSelectedColorHex(e.target.value)}
                        style={{
                          width: "50px",
                          height: "40px",
                          cursor: "pointer",
                        }}
                      />
                    </div>
                    <div
                      style={{ display: "flex", gap: "8px", flexWrap: "wrap" }}
                    >
                      {predefinedColors.map((color) => (
                        <button
                          key={color.hex}
                          type="button"
                          onClick={() => addColor(color)}
                          style={{
                            width: "40px",
                            height: "40px",
                            backgroundColor: color.hex,
                            border: "2px solid #ccc",
                            borderRadius: "4px",
                            cursor: "pointer",
                            title: color.name,
                          }}
                          title={color.name}
                        />
                      ))}
                    </div>
                    <button
                      type="button"
                      className="x_btn x_btn-primary"
                      onClick={() =>
                        addColor({ name: "Custom", hex: selectedColorHex })
                      }
                      style={{
                        marginTop: "10px",
                        padding: "6px 10px",
                        fontSize: "12px",
                      }}
                    >
                      Add Custom Color
                    </button>
                  </div>
                )}

                {formData.colors && formData.colors.length > 0 && (
                  <div
                    style={{
                      display: "flex",
                      flexWrap: "wrap",
                      gap: "10px",
                      marginTop: "10px",
                      alignItems: "center",
                    }}
                  >
                    {formData.colors.map((color) => (
                      <div
                        key={color.hex}
                        style={{
                          display: "flex",
                          alignItems: "center",
                          gap: "8px",
                          padding: "6px 10px",
                          backgroundColor: "#f0f0f0",
                          borderRadius: "4px",
                          border: `3px solid ${color.hex}`,
                        }}
                      >
                        <div
                          style={{
                            width: "20px",
                            height: "20px",
                            backgroundColor: color.hex,
                            borderRadius: "3px",
                            border: "1px solid #999",
                          }}
                        />
                        <span style={{ fontSize: "13px" }}>{color.name}</span>
                        <button
                          type="button"
                          onClick={() => removeColor(color.hex)}
                          style={{
                            backgroundColor: "transparent",
                            border: "none",
                            cursor: "pointer",
                            color: "#ff0000",
                            fontSize: "16px",
                            fontWeight: "bold",
                          }}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Sizes Section */}
              <div className="x_form-group">
                <label className="x_form-label">
                  Sizes (comma separated, e.g. 36,38,40)
                </label>
                <input
                  type="text"
                  name="sizes"
                  className="x_form-control"
                  value={Array.isArray(formData.sizes) ? formData.sizes.join(",") : ""}
                  onChange={handleInputChange}
                  placeholder="Enter available sizes like 36,38,40"
                />
              </div>

              {/* Basic Product Info */}
              <div className="x_form-group">
                <label className="x_form-label">Product Name *</label>
                <input
                  type="text"
                  name="name"
                  className="x_form-control"
                  value={formData.name}
                  onChange={handleInputChange}
                  placeholder="Enter product name"
                  required
                />
              </div>

              <div className="x_form-group">
                <label className="x_form-label">Category</label>
                <select
                  name="category"
                  className="x_form-select"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  <option value="">Select category</option>
                  {categories.map((cat) => (
                    <option key={cat._id} value={cat.name}>{cat.name}</option>
                  ))}
                </select>
              </div>

              {/* Pricing Section */}
              <div
                style={{
                  borderTop: "1px solid #ddd",
                  paddingTop: "15px",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ marginBottom: "12px" }}>Pricing & Offers</h4>

                <div className="x_form-group">
                  <label className="x_form-label">Price (Base) *</label>
                  <input
                    type="number"
                    name="price"
                    className="x_form-control"
                    value={formData.price}
                    onChange={handleInputChange}
                    placeholder="e.g., 99.99"
                    step="0.01"
                    required
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Discount % (Optional)</label>
                  <input
                    type="number"
                    name="discountPercent"
                    className="x_form-control"
                    value={formData.discountPercent}
                    onChange={handleInputChange}
                    placeholder="e.g., 10"
                    min="0"
                    max="100"
                    step="0.01"
                  />
                  <small style={{ color: "#666" }}>
                    Sale price will auto-calculate
                  </small>
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">
                    Sale Price (Auto-calculated)
                  </label>
                  <input
                    type="number"
                    name="salePrice"
                    className="x_form-control"
                    value={formData.salePrice}
                    disabled
                    placeholder="Auto-calculated"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Rating (Optional)</label>
                  <input
                    type="number"
                    name="rating"
                    className="x_form-control"
                    value={formData.rating}
                    onChange={handleInputChange}
                    placeholder="e.g., 4.5"
                    min="0"
                    max="5"
                    step="0.1"
                  />
                </div>
              </div>

              {/* Stock & Status */}
              <div
                style={{
                  borderTop: "1px solid #ddd",
                  paddingTop: "15px",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ marginBottom: "12px" }}>Stock & Status</h4>

                <div className="x_form-group">
                  <label className="x_form-label">Stock Quantity *</label>
                  <input
                    type="number"
                    name="stock"
                    className="x_form-control"
                    value={formData.stock}
                    onChange={handleInputChange}
                    placeholder="Enter stock quantity"
                    required
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Status</label>
                  <select
                    name="status"
                    className="x_form-select"
                    value={formData.status}
                    onChange={handleInputChange}
                  >
                    <option value="Active">Active</option>
                    <option value="Inactive">Inactive</option>
                    <option value="Out of Stock">Out of Stock</option>
                  </select>
                </div>
              </div>

              {/* Product Information */}
              <div
                style={{
                  borderTop: "1px solid #ddd",
                  paddingTop: "15px",
                  marginTop: "15px",
                }}
              >
                <h4 style={{ marginBottom: "12px" }}>Product Information</h4>

                <div className="x_form-group">
                  <label className="x_form-label">Product Description</label>
                  <textarea
                    name="description"
                    className="x_form-control"
                    value={formData.description}
                    onChange={handleInputChange}
                    placeholder="Enter detailed product description"
                    rows="3"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Fabric</label>
                  <input
                    type="text"
                    name="fabric"
                    className="x_form-control"
                    value={formData.fabric}
                    onChange={handleInputChange}
                    placeholder="e.g., Cotton, Polyester, Silk"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Manufacturer Name</label>
                  <input
                    type="text"
                    name="manufacturer"
                    className="x_form-control"
                    value={formData.manufacturer}
                    onChange={handleInputChange}
                    placeholder="Enter manufacturer name"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Occasion</label>
                  <input
                    type="text"
                    name="occasion"
                    className="x_form-control"
                    value={formData.occasion}
                    onChange={handleInputChange}
                    placeholder="e.g., Daily Wear, Formal, Casual"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Wash Care</label>
                  <textarea
                    name="washCare"
                    className="x_form-control"
                    value={formData.washCare}
                    onChange={handleInputChange}
                    placeholder="Enter care instructions"
                    rows="2"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Product Type</label>
                  <input
                    type="text"
                    name="productType"
                    className="x_form-control"
                    value={formData.productType}
                    onChange={handleInputChange}
                    placeholder="e.g., T-Shirt, Jeans, Dress"
                  />
                </div>

                <div className="x_form-group">
                  <label className="x_form-label">Work/Design Details</label>
                  <input
                    type="text"
                    name="work"
                    className="x_form-control"
                    value={formData.work}
                    onChange={handleInputChange}
                    placeholder="e.g., Embroidered, Printed, Solid"
                  />
                </div>
              </div>
            </div>

            <div className="x_modal-footer">
              <button
                type="button"
                className="x_btn x_btn-secondary"
                onClick={resetForm}
              >
                Cancel
              </button>
              <button type="submit" className="x_btn x_btn-primary">
                {editingId ? "Update" : "Create"}
              </button>
            </div>
          </form>
        </div>
      </div>

      {/* Table */}
      {/* Table Section */}

      <style>
        {`
       .x_table-wrapper {
           max-width: 89vw;
           overflow-x: auto; 
           background: #ffffff;
           box-shadow: 0 2px 10px rgba(0,0,0,0.08);
           border: 1px solid #e0e0e0;
       }
       
       
       .x_table-wrapper::-webkit-scrollbar {
           height: 6px;
       }
       .x_table-wrapper::-webkit-scrollbar-track {
           background: #f1f1f1;
       }
       .x_table-wrapper::-webkit-scrollbar-thumb {
           background: #ccc;
           border-radius: 10px;
       }
       .x_table-wrapper::-webkit-scrollbar-thumb:hover {
           background: #aaa;
       }
      
       .x_data-table {
           width: 100%;
           min-width: 1000px; 
           border-collapse: collapse;
           text-align: left;
       }
       
       .x_data-table th {
           background-color: #f8f9fa;
           padding: 15px 12px;
           font-weight: 600;
           color: #2b4d6e;
           border-bottom: 2px solid #dee2e6;
           white-space: nowrap;
       }
       
       .x_data-table td {
           padding: 12px;
           border-bottom: 1px solid #eee;
           vertical-align: middle;
           color: #444;
       }
       .x_data-table tbody tr:hover {
           background-color: #fcfcfc;
       }
       
       @media (max-width: 1350px) and (min-width: 769px) {
           .x_table-wrapper {
           max-width: calc(100vw - 360px); 
          }
          }
       `}
      </style>
      {/* Table Section */}
      <div className="x_card ">
        <div className="x_card-body">
          <div className="x_table-wrapper">
            <table className="x_data-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th>Category</th>
                  <th>Price</th>
                  <th>Stock</th>
                  <th>Status</th>
                  <th>Colors</th>
                  <th style={{ textAlign: "center" }}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentProducts.map((product) =>
                (<tr key={product._id}>
                  {/* Product Image & Name */}
                  <td>
                    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
                      <img
                        src={
                          product.images && product.images.length > 0
                            ? product.images[0]
                            : "https://via.placeholder.com/45"
                        }
                        alt={product.title || "Product"}
                        style={{
                          width: "45px",
                          height: "45px",
                          borderRadius: "6px",
                          objectFit: "cover",
                          border: "1px solid #eee",
                        }}
                      />
                      <span style={{ fontWeight: "500" }}>{product.title || product.name || "Unnamed"}</span>
                    </div>
                  </td>

                  {/* Category */}
                  <td>{getCategoryName(product)}</td>

                  {/* Price */}
                  <td>
                    <div style={{ lineHeight: "1.2" }}>
                      <div style={{ fontWeight: "700", color: "#2b4d6e" }}>
                        ${product.salePrice}
                      </div>
                      {product.discountPercent > 0 && (
                        <div
                          style={{
                            fontSize: "11px",
                            color: "#999",
                            textDecoration: "line-through",
                          }}
                        >
                          ${product.price}
                        </div>
                      )}
                    </div>
                  </td>

                  {/* Stock */}
                  <td style={{ fontWeight: "500" }}>{product.stock}</td>

                  {/* Status */}
                  <td>
                    <span
                      style={{
                        padding: "4px 10px",
                        borderRadius: "20px",
                        fontSize: "12px",
                        fontWeight: "500",
                        backgroundColor: product.active ? "#e6f4ea" : "#feeaee",
                        color: product.active ? "#1e7e34" : "#d93025",
                      }}
                    >
                      {product.active ? "Active" : "Inactive"}
                    </span>
                  </td>

                  {/* Colors */}
                  <td>
                    <div style={{ display: "flex", gap: "5px" }}>
                      {product.colors.map((c, i) => (
                        <div
                          key={i}
                          title={c.name}
                          style={{
                            width: "16px",
                            height: "16px",
                            borderRadius: "50%",
                            backgroundColor: c.hex,
                            border: "1px solid #ddd",
                            boxShadow: "inset 0 0 2px rgba(0,0,0,0.1)",
                          }}
                        />
                      ))}
                    </div>
                  </td>

                  {/* Actions */}
                  <td style={{ textAlign: "center" }}>
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "center",
                        gap: "12px",
                      }}
                      className="td_btnrm"
                    >
                      <button
                        onClick={() => handleEdit(product)}
                        className="btn_edit"
                        title="Edit"
                      >
                        <FiEdit2 size={18} />
                      </button>
                      <button
                        onClick={() => handleDeleteClick(product._id)}
                        className="btn_remove"
                        title="Delete"
                      >
                        <FiTrash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Pagination */}
        {products.length > ITEMS_PER_PAGE && (
          <div className="x_pagination">
            <button
              className={`x_pagination-item ${currentPage === 1 ? "x_active" : ""}`}
              onClick={() => setCurrentPage(1)}
            >
              1
            </button>

            {currentPage > 3 && (
              <span className="x_pagination-dots">...</span>
            )}

            {Array.from({ length: totalPages }, (_, i) => i + 1)
              .filter(
                (page) =>
                  page !== 1 &&
                  page !== totalPages &&
                  page >= currentPage - 1 &&
                  page <= currentPage + 1
              )
              .map((page) => (
                <button
                  key={page}
                  className={`x_pagination-item ${currentPage === page ? "x_active" : ""}`}
                  onClick={() => setCurrentPage(page)}
                >
                  {page}
                </button>
              ))}

            {currentPage < totalPages - 2 && (
              <span className="x_pagination-dots">...</span>
            )}

            {totalPages > 1 && (
              <button
                className={`x_pagination-item ${currentPage === totalPages ? "x_active" : ""}`}
                onClick={() => setCurrentPage(totalPages)}
              >
                {totalPages}
              </button>
            )}
          </div>
        )}
      </div>

      <Modal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        onConfirm={handleDeleteConfirm}
        title="Delete Product"
        message="Are you sure you want to delete this product?"
        confirmText="Yes, Delete"
        cancelText="Cancel"
      />
    </div>
  );
}

export default Products;
